import { useFieldSelectOptions } from "../../../utils/useFieldSelectOptions";
import { MultiSelectFilter } from "../../../components/general-filters/MultiSelectFilter";

type CategoryGroup = {
	key: string;
	label: string;
	professionalCategories: Array<string>;
	clientOwnerSpecializations: Array<string>;
};

const CATEGORY_GROUPS: Array<CategoryGroup> = [
	{
		key: "neurofeedback",
		label: "Neurofeedback",
		professionalCategories: ["neurofeedback_technician"],
		clientOwnerSpecializations: ["neurofeedback"],
	},
	{
		key: "psicologo",
		label: "Psicólogo",
		professionalCategories: ["psychologist"],
		clientOwnerSpecializations: ["psicologia"],
	},
	{
		key: "supervisores",
		label: "Supervisores",
		professionalCategories: [
			"supervisor_client_success",
			"supervisor_operations",
			"supervisor_neurofeedback",
			"supervisor_marketing",
		],
		clientOwnerSpecializations: [],
	},
];

const GROUP_PREFIX = "group:";
const CATEGORY_PREFIX = "pc:";

const groupedProfessionalCategories = new Set(
	CATEGORY_GROUPS.flatMap((group) => group.professionalCategories)
);

type ProfessionalCategoryFilterProps = {
	selected: Array<string>;
	onChange: (values: Array<string>) => void;
};

export const ProfessionalCategoryFilter = ({ selected, onChange }: ProfessionalCategoryFilterProps) => {
	const professionalCategoryChoices = useFieldSelectOptions("staff_agreements", "professional_category");

	const groupOptions = CATEGORY_GROUPS.map((group) => ({
		value: `${GROUP_PREFIX}${group.key}`,
		label: group.label,
	}));

	const looseCategoryOptions = professionalCategoryChoices
		.filter((choice) => !groupedProfessionalCategories.has(choice.value))
		.map((choice) => ({
			value: `${CATEGORY_PREFIX}${choice.value}`,
			label: choice.text,
		}));

	const options = [...groupOptions, ...looseCategoryOptions].sort((a, b) =>
		a.label.localeCompare(b.label)
	);

	return (
		<MultiSelectFilter
			label="Categoria Profissional"
			options={options}
			selected={selected}
			onChange={onChange}
			size="medium"
			fullWidth
		/>
	);
};

export const matchesAgreementSelection = (
	selected: Array<string>,
	professionalCategory: string | undefined,
	technicianSpecializations: Array<string>,
) => {
	if (selected.length === 0) return true;
	if (!professionalCategory) return false;

	const isClientOwner = professionalCategory === "client_owner";

	for (const selectedValue of selected) {
		if (selectedValue.startsWith(CATEGORY_PREFIX)) {
			const looseCategory = selectedValue.slice(CATEGORY_PREFIX.length);

			if (professionalCategory === looseCategory) return true;
			continue;
		}

		if (selectedValue.startsWith(GROUP_PREFIX)) {
			const groupKey = selectedValue.slice(GROUP_PREFIX.length);
			const group = CATEGORY_GROUPS.find((candidate) => candidate.key === groupKey);

			if (!group) continue;

			if (group.professionalCategories.includes(professionalCategory)) return true;
			if (isClientOwner && group.clientOwnerSpecializations.some((spec) => technicianSpecializations.includes(spec))) {
				return true;
			}
		}
	}

	return false;
};
